import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import { useConfirmationModalStore } from '../../../../../../stores';
import { updateTeamRuntime, excludeTeam, deleteTeamSanction } from '../../../../../../views/democracy/controlsDispatcher';
import { buildJoinUrl, buildMediaUrl, extractErrorMessage } from '../../../../../../views/democracy/utils';
import Modal from '../../../controls/Modal';
import LabeledInput from '../../../controls/LabeledInput';
import HmsInput from '../../../controls/HmsInput';

// Модалка команды на пульте: runtime (баланс/рейтинг/время), активные санкции, исключение.
export default function TeamRuntimeModal({ team, democracyId, onClose }) {
    const { enqueueSnackbar } = useSnackbar();
    const { open: openConfirmation } = useConfirmationModalStore();
    const teamId = team.teamId || team.id;

    const [data, setData] = useState({
        balance: team.balance,
        rating: team.rating,
        remainingSeconds: team.remainingSeconds ?? 0
    });
    const [sanctions, setSanctions] = useState(team.activeSanctions || []);

    const setField = (field, value) => setData(prev => ({ ...prev, [field]: value }));

    const handleSave = async () => {
        try {
            await updateTeamRuntime(democracyId, teamId, {
                balance: parseInt(data.balance, 10) || 0,
                rating: parseInt(data.rating, 10) || 0,
                remainingSeconds: parseInt(data.remainingSeconds, 10) || 0
            });
            enqueueSnackbar('Сохранено', { variant: 'success' });
            onClose();
        } catch (error) {
            enqueueSnackbar(extractErrorMessage(error, 'Не удалось сохранить'), { variant: 'error' });
        }
    };

    const handleExclude = () => {
        openConfirmation(`Исключить команду «${team.name}» из активной игры?`, async () => {
            try {
                await excludeTeam(democracyId, teamId);
                enqueueSnackbar('Команда исключена', { variant: 'success' });
                onClose();
            } catch (error) {
                enqueueSnackbar(extractErrorMessage(error, 'Не удалось исключить'), { variant: 'error' });
            }
        });
    };

    const handleDeleteSanction = async (sanctionId) => {
        try {
            await deleteTeamSanction(democracyId, teamId, sanctionId);
            setSanctions(list => list.filter(s => s.sanctionId !== sanctionId && s.id !== sanctionId));
            enqueueSnackbar('Санкция снята', { variant: 'success' });
        } catch (error) {
            enqueueSnackbar(extractErrorMessage(error, 'Не удалось снять санкцию'), { variant: 'error' });
        }
    };

    const footer = (
        <>
            <button type="button" className="btn btn-danger" onClick={handleExclude}>Исключить из игры</button>
            <button type="button" className="btn btn-primary" onClick={handleSave}>Сохранить</button>
        </>
    );

    return (
        <Modal open title={`Команда «${team.name}»`} onClose={onClose} size="lg" footer={footer}>
            <div className="row-3">
                <LabeledInput label="Баланс" value={String(data.balance)} onChange={(e) => setField('balance', e.target.value)} />
                <LabeledInput label="Рейтинг" value={String(data.rating)} onChange={(e) => setField('rating', e.target.value)} />
                <HmsInput label="Время" seconds={data.remainingSeconds}
                    onChange={(secs) => setField('remainingSeconds', secs)} />
            </div>

            <div>
                <label className="ctl-label">Активные санкции</label>
                {sanctions.length === 0 ? (
                    <div className="t-caption">Санкций нет</div>
                ) : (
                    <div className="tsanc-grid">
                        {sanctions.map((s, i) => (
                            <div className="tsanc" key={s.sanctionId || s.id || i}
                                style={s.media?.url ? { backgroundImage: `url(${buildMediaUrl(s.media.url)})` } : undefined}>
                                <div className="meta">
                                    <div className="nm">{s.title}</div>
                                    <div className="lf">Осталось: {s.durationLeft ?? s.duration}</div>
                                </div>
                                <button type="button" className="cbtn cbtn-red" onClick={() => handleDeleteSanction(s.sanctionId || s.id)}>Снять</button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {team.joinCode && (
                <div>
                    <label className="ctl-label">Подключение по joinCode (тест)</label>
                    <a href={buildJoinUrl(team.joinCode)} target="_blank" rel="noreferrer" style={{ wordBreak: 'break-all' }}>
                        {buildJoinUrl(team.joinCode)}
                    </a>
                </div>
            )}
        </Modal>
    );
}

TeamRuntimeModal.propTypes = {
    team: PropTypes.object.isRequired,
    democracyId: PropTypes.number.isRequired,
    onClose: PropTypes.func.isRequired
};
